import {
  Badge,
  Box,
  Button,
  FormControl,
  FormLabel,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Square,
  Avatar,
  Text,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import {
  AsyncSelect,
  chakraComponents,
  type OptionProps,
  type CSSObjectWithLabel,
} from 'chakra-react-select';
import { useCallback, useMemo, useState } from 'react';
import { getUserFullName } from '@duke-hq/lib';
import { ProviderUserRoleColorSchemes } from '@duke-hq/shared-constants/providers';
import { useDebounceCallback } from 'usehooks-ts';
import { trpcAdmin } from '../../lib/trpcAdmin';

interface ConsultantOption {
  value: string;
  label: string | undefined;
  fullname: string | undefined;
  email: string | null;
  picture_url: string | null;
  role: string;
}

function ConsultantOptionComponent(props: OptionProps<ConsultantOption>) {
  return (
    <chakraComponents.Option {...props}>
      <Box display="flex" alignItems="center" justifyContent="space-between" w="full">
        <Box display="flex" alignItems="center">
          <Square size="32px">
            <Avatar
              size="sm"
              name={props.data.fullname}
              src={props.data.picture_url ?? undefined}
            />
          </Square>
          <VStack align="flex-start" gap={0} ml={4}>
            <Text fontWeight="medium">{props.data.label}</Text>
            <Text fontSize="sm" color={props.isSelected ? 'fg.inverse-subtle' : 'fg.muted'}>
              {props.data.email}
            </Text>
          </VStack>
        </Box>
        <Box display="flex" alignItems="flex-end">
          <Badge
            borderRadius="full"
            colorScheme={
              (props.data.role && ProviderUserRoleColorSchemes[props.data.role]) ?? 'gray'
            }
            variant="muted"
            textTransform="none"
          >
            {props.data.role?.replace(/_/g, ' ')}
          </Badge>
        </Box>
      </Box>
    </chakraComponents.Option>
  );
}

interface BulkTransferConsultantModalProps {
  isOpen: boolean;
  onClose: () => void;
  consultantId: string;
  providerId: number;
  enrolmentCount: number;
  referralCount: number;
}

export function BulkTransferConsultantModal({
  isOpen,
  onClose,
  consultantId,
  providerId,
  enrolmentCount,
  referralCount,
}: BulkTransferConsultantModalProps) {
  const utils = trpcAdmin.useUtils();
  const toast = useToast();

  const [selectedConsultantId, setSelectedConsultantId] = useState<string | null>(null);

  const { data: consultants, isFetching: isConsultantsLoading } =
    trpcAdmin.providers.user.listConsultants.useQuery({ providerId }, { enabled: isOpen });

  const consultantOptions: ConsultantOption[] = useMemo(() => {
    if (!consultants) {
      return [];
    }
    return consultants
      .filter((c) => c.id !== consultantId)
      .map((consultant) => ({
        value: consultant.id,
        label: getUserFullName(consultant),
        fullname: getUserFullName(consultant),
        email: consultant.email,
        picture_url: consultant.picture_url,
        role: consultant.role,
      }));
  }, [consultants, consultantId]);

  const loadConsultantOptions = useDebounceCallback(
    (inputValue: string, callback: (options: ConsultantOption[]) => void) => {
      if (consultantOptions.length > 0) {
        callback(
          consultantOptions.filter(
            (option) =>
              (option.label != null &&
                option.label.toLowerCase().includes(inputValue.toLowerCase())) ||
              (option.email != null &&
                option.email.toLowerCase().includes(inputValue.toLowerCase())),
          ),
        );
      } else {
        callback([]);
      }
    },
    500,
  );

  const disableConsultant = trpcAdmin.providers.user.disableConsultant.useMutation({
    onSuccess: () => {
      toast({
        title: t`Consultant disabled and assignments transferred successfully.`,
        status: 'success',
        isClosable: true,
      });

      void utils.providers.user.listConsultantsWithPagination.refetch();
      void utils.providers.user.consultantById.refetch();
      void utils.providers.user.consultantActiveAssignmentCounts.invalidate();
      handleClose();
    },
    onError: (error) => {
      toast({
        title: t`Error transferring assignments`,
        description:
          error instanceof Error ? error.message : t`Something went wrong. Please contact support.`,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    },
  });

  const handleClose = useCallback(() => {
    setSelectedConsultantId(null);
    onClose();
  }, [onClose]);

  const handleSubmit = useCallback(() => {
    if (!selectedConsultantId) {
      return;
    }
    disableConsultant.mutate({
      id: consultantId,
      providerId,
      transferToConsultantId: selectedConsultantId,
    });
  }, [selectedConsultantId, consultantId, providerId, disableConsultant]);

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <Trans>Transfer and Disable Consultant</Trans>
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody>
          <VStack align="stretch" spacing={4}>
            <Text fontSize="sm" color="fg.muted">
              <Trans>
                {enrolmentCount} active enrolments and {referralCount} active referrals will be
                transferred to the selected consultant.
              </Trans>
            </Text>

            <FormControl isRequired>
              <FormLabel>
                <Trans>Transfer to</Trans>
              </FormLabel>
              <AsyncSelect
                placeholder={t`Select consultant`}
                loadOptions={loadConsultantOptions}
                defaultOptions={consultantOptions}
                value={consultantOptions.find((o) => o.value === selectedConsultantId) ?? null}
                onChange={(val: ConsultantOption | null) => {
                  setSelectedConsultantId(val?.value ?? null);
                }}
                components={{ Option: ConsultantOptionComponent }}
                isLoading={isConsultantsLoading}
                menuPortalTarget={document.body}
                styles={{
                  menuPortal: (base: CSSObjectWithLabel) => ({
                    ...base,
                    zIndex: 9999,
                  }),
                }}
              />
            </FormControl>
          </VStack>
        </ModalBody>

        <ModalFooter>
          <HStack spacing={2}>
            <Button variant="ghost" onClick={handleClose}>
              <Trans>Cancel</Trans>
            </Button>
            <Button
              colorScheme="red"
              onClick={handleSubmit}
              isLoading={disableConsultant.isPending}
              isDisabled={!selectedConsultantId}
            >
              <Trans>Transfer and Disable</Trans>
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
